import React, { useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Bell, X, MessageCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";

/**
 * PushNotification component for displaying Firebase push notifications
 * Appears on the right side with site colors
 */
const PushNotification = ({
  title,
  body,
  isVisible,
  onClose,
  duration = 5000,
  data = {},
  onClick,
  stacked = false, // when true, no fixed positioning (used inside a stack container)
}) => {
  const navigate = useNavigate();

  useEffect(() => {
    if (isVisible && duration > 0) {
      const timer = setTimeout(() => {
        onClose();
      }, duration);
      return () => clearTimeout(timer);
    }
  }, [isVisible, duration, onClose]);

  const handleClick = () => {
    if (onClick) {
      onClick();
    } else if (data.url) {
      navigate(data.url);
    } else if (data.channelId) {
      navigate("/communication/chat");
    }
    onClose();
  };

  if (!isVisible) return null;

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0, x: 400, scale: 0.9 }}
          animate={{ opacity: 1, x: 0, scale: 1 }}
          exit={{ opacity: 0, x: 400, scale: 0.9 }}
          transition={{ type: "spring", damping: 25, stiffness: 300 }}
          className={stacked ? "min-w-[320px] max-w-[420px]" : "fixed top-4 right-4 z-[9999] min-w-[320px] max-w-[420px]"}
        >
          <div
            onClick={handleClick}
            className="bg-white dark:bg-dark-100 border border-gray-200 dark:border-dark-200 rounded-lg shadow-2xl overflow-hidden cursor-pointer hover:shadow-3xl transition-all duration-200 hover:scale-[1.02]"
          >
            {/* Header with icon */}
            <div className="flex items-start gap-3 p-4 bg-gradient-to-r from-primary-accent/10 to-primary-accent/5 dark:from-primary-accent/20 dark:to-primary-accent/10 border-b border-gray-200 dark:border-dark-200">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary-accent/20 dark:bg-primary-accent/30 flex items-center justify-center">
                <Bell className="w-5 h-5 text-primary-accent" />
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="text-sm font-semibold text-gray-900 dark:text-light-100 truncate">
                  {title || "New Notification"}
                </h4>
                {body && (
                  <p className="text-xs text-gray-600 dark:text-light-400 mt-1 line-clamp-2">
                    {body}
                  </p>
                )}
              </div>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onClose();
                }}
                className="flex-shrink-0 p-1 hover:bg-gray-100 dark:hover:bg-dark-200 rounded transition-colors"
                aria-label="Close notification"
              >
                <X className="w-4 h-4 text-gray-400 dark:text-light-500" />
              </button>
            </div>

            {/* Footer hint */}
            {(data.url || data.channelId) && (
              <div className="px-4 py-2 bg-gray-50 dark:bg-dark-200/50 border-t border-gray-200 dark:border-dark-200">
                <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-light-500">
                  <MessageCircle className="w-3 h-3" />
                  <span>Click to open</span>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default PushNotification;
